import {
  Button,
  Chip,
  Dropdown,
  Grid,
  GridColumn,
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import {
  CURRENT_YEAR,
  NEXT_YEAR,
  RESERVATION_HISTORY_COLUMN_HEADERS,
  YEAR_FILTER_OPTIONS
} from "@/constants";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { SortOrder } from "@/types/common";
import {
  Reservation,
  ReservationList,
  ReservationSortableColumnKeys,
  ReservationStatus
} from "@/types/Reservation";
import { getReservationsBySoldToId } from "@/utils/Reservation/ReservationApi";

import { setReservationConversionID } from "../../features/globalStates/globalStatesSlice";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { Pagination } from "./Pagination";

const ActionsComponent: FC<{ reservation: Reservation }> = ({
  reservation
}) => {
  const isReservationConversionEnabled = useFeatureFlag(
    "IS_RESERVATIONS_CONVERSION_ENABLED"
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const locale = useSelector((state: any) => state.globalStates.locale);
  const status = reservation.reservationStatus;
  const dispatch = useDispatch();
  const history = useHistory();

  const handleReservationConversionClick = async (reservationId: String) => {
    dispatch(setReservationConversionID(reservationId));
    history.push(`/${locale}/order-form/complete-reservation`);
  };

  return (
    <>
      {isReservationConversionEnabled ? (
        status === ReservationStatus.Pending && (
          <Button
            onClick={() =>
              handleReservationConversionClick(reservation?.reservationId)
            }
            variant="outline"
          >
            Order Now
          </Button>
        )
      ) : (
        <Button
          onClick={() => {
            if (status === ReservationStatus.Cancelled) {
              history.push(`/${locale}/summary/${reservation?.reservationId}`);
            } else {
              history.push(
                `/${locale}/reservation/${reservation?.reservationId}`
              );
            }
          }}
          variant="subtle"
        >
          {status === ReservationStatus.Cancelled ? "View" : "View/Edit"}
        </Button>
      )}
    </>
  );
};

export const ReservationTable: FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userData = useSelector((store: any) => store?.commerce?.userData);
  const [reservationList, setReservationList] = useState<ReservationList>();
  const [reservationError, setReservationError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageKeyMap, setPageKeyMap] = useState<Map<number, string>>(new Map());
  const [sortDirection, setSortDirection] = useState<SortOrder>(SortOrder.DESC);
  const [sortProperty, setSortProperty] =
    useState<ReservationSortableColumnKeys>(
      ReservationSortableColumnKeys.DatePlaced
    );

  const userId = userData?.userId;
  const soldToId = userData?.soldToAddress?.organizationId;

  const PAGE_SIZE = 10;

  // todo: MD-623 integrate with get reservation by sold to api with year filter
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [yearFilter, setYearFilter] = useState<string>(
    YEAR_FILTER_OPTIONS.CURRENT_YEAR
  );
  const [isLoadingReservations, setIsLoadingReservations] =
    useState<boolean>(false);

  const reservations = reservationList?.reservationsWithUsers || [];
  const totalPage = reservationList?.totalPages || 0;

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        setIsLoadingReservations(true);
        const pageStartKey = pageKeyMap.get(currentPage);
        // todo: MD-623 integrate with BE for sorting and year filter
        const data = await getReservationsBySoldToId({
          userId,
          soldToId,
          pageSize: PAGE_SIZE,
          pageStartKey
        });
        setReservationList(data);
        setPageKeyMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.set(currentPage + 1, data.pagination.nextPageKey || "");
          return newMap;
        });
      } catch (error) {
        setReservationError(
          (error as Error)?.message || "Failed to fetch reservations"
        );
      } finally {
        setIsLoadingReservations(false);
      }
    };

    fetchReservations();
    // excluding the dependency on `pageKeyMap` to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, soldToId, userId]);

  const getReservationStatus = (status: ReservationStatus) => {
    const formattedStatus =
      status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    switch (status) {
      case ReservationStatus.Cancelled:
        return <Chip label={formattedStatus} />;
      case ReservationStatus.Pending:
        return <Text sx={{ pl: "spacing-3" }}>{formattedStatus}</Text>;
      default:
        return <Chip variant="success" label={formattedStatus} />;
    }
  };

  const handleSortClick = (property: ReservationSortableColumnKeys) => {
    const newSortDirection =
      sortProperty === property && sortDirection === SortOrder.ASC
        ? SortOrder.DESC
        : SortOrder.ASC;
    setSortDirection(newSortDirection);
    setSortProperty(property);
  };

  const currentYearOption = {
    label: `${CURRENT_YEAR}-${NEXT_YEAR}`,
    value: YEAR_FILTER_OPTIONS.CURRENT_YEAR
  };

  const pastYearsOption = {
    label: `2024-${CURRENT_YEAR}`,
    value: YEAR_FILTER_OPTIONS.PAST_YEARS
  };

  return (
    <ThemeProvider>
      <Grid>
        <GridColumn sx={{ pt: "spacing-6" }}>
          <Text variant="h4">Reservations</Text>
        </GridColumn>
        <GridColumn size={3} sx={{ pt: "spacing-6" }}>
          <Dropdown
            label="Select Year"
            dropdownOptions={[currentYearOption, pastYearsOption]}
            defaultOption={currentYearOption}
            onSelectOption={(yearOption) => {
              setYearFilter(String(yearOption.value));
              setCurrentPage(1);
            }}
          />
        </GridColumn>
        <GridColumn sx={{ pt: "spacing-6" }}>
          {isLoadingReservations ? (
            <LoadingSpinner size="xl" />
          ) : reservationError ? (
            <Text sx={{ pt: "spacing-6", color: "color-system-red-700" }}>
              {reservationError}
            </Text>
          ) : reservations.length === 0 ? (
            <Text sx={{ padding: "spacing-6" }}>No reservations found.</Text>
          ) : (
            <Grid>
              <GridColumn size={12}>
                <Table spacing="default">
                  <TableHeaderRow>
                    <TableRow>
                      <TableHeaderCell key="reservationId">
                        {RESERVATION_HISTORY_COLUMN_HEADERS.RESERVATION_ID}
                      </TableHeaderCell>
                      <TableHeaderCell key="poNumber">
                        {RESERVATION_HISTORY_COLUMN_HEADERS.PO_NUMBER}
                      </TableHeaderCell>
                      <TableHeaderCell key="placedBy">
                        {RESERVATION_HISTORY_COLUMN_HEADERS.PLACED_BY}
                      </TableHeaderCell>
                      <TableHeaderCell key="doses">
                        {RESERVATION_HISTORY_COLUMN_HEADERS.DOSES}
                      </TableHeaderCell>
                      <TableHeaderCell
                        key="datePlaced"
                        onClick={() =>
                          handleSortClick(
                            ReservationSortableColumnKeys.DatePlaced
                          )
                        }
                        sortable={true}
                        sortKey={ReservationSortableColumnKeys.DatePlaced}
                        sortDirection={
                          sortProperty ===
                          ReservationSortableColumnKeys.DatePlaced
                            ? sortDirection
                            : undefined
                        }
                      >
                        {RESERVATION_HISTORY_COLUMN_HEADERS.DATE_PLACED}
                      </TableHeaderCell>
                      <TableHeaderCell
                        key="status"
                        onClick={() =>
                          handleSortClick(ReservationSortableColumnKeys.Status)
                        }
                        sortable={true}
                        sortKey={ReservationSortableColumnKeys.Status}
                        sortDirection={
                          sortProperty === ReservationSortableColumnKeys.Status
                            ? sortDirection
                            : undefined
                        }
                      >
                        {RESERVATION_HISTORY_COLUMN_HEADERS.STATUS}
                      </TableHeaderCell>
                      <TableHeaderCell key="actions">
                        {RESERVATION_HISTORY_COLUMN_HEADERS.ACTIONS}
                      </TableHeaderCell>
                    </TableRow>
                  </TableHeaderRow>
                  <TableBody>
                    {reservations.map((reservationWithUser, index) => {
                      const { reservation, placedByUser } = reservationWithUser;
                      const {
                        reservationNumber,
                        purchaseOrderNumber,
                        lastModifiedDate,
                        reservationStatus,
                        reservationPricingInfo
                      } = reservation;
                      const { firstName, lastName } = placedByUser;

                      return (
                        <TableRow key={index}>
                          <TableCell key="reservationNo">
                            {reservationNumber || "--"}
                          </TableCell>
                          <TableCell key="poNumber">
                            {purchaseOrderNumber || "--"}
                          </TableCell>
                          <TableCell key="placedBy">{`${firstName ?? "-"} ${lastName ?? "-"}`}</TableCell>
                          <TableCell key="doeses">
                            {reservationPricingInfo?.totalDoses}
                          </TableCell>
                          <TableCell key="datePlaced">
                            {lastModifiedDate
                              ? lastModifiedDate.split("T")[0] || "--"
                              : "--"}
                          </TableCell>
                          <TableCell key="status">
                            {getReservationStatus(reservationStatus)}
                          </TableCell>
                          <TableCell key="actions">
                            <ActionsComponent reservation={reservation} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </GridColumn>
              <Grid sx={{ mt: "spacing-9" }}>
                <GridColumn push={[0, 3, 5]} size={2}>
                  <Pagination
                    page={currentPage}
                    count={totalPage}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </GridColumn>
              </Grid>
            </Grid>
          )}
        </GridColumn>
      </Grid>
    </ThemeProvider>
  );
};
